import * as React from 'react';

import { StartFilled, Stop, Undo16, Delete16, Archive16, FolderMove16 } from 'ui/icons';
import { BulkActionsBar } from 'ui/bulkActionsBar';

import { FolderId, FolderIdData } from 'modules/domains/foldering';
import { WorkflowType } from 'modules/workflows/enums';
import { WorkflowId, WorkflowIdData, WorkflowStatusData } from '../types';

const { useMemo } = React;

const displayText = {
  enable: 'Turn on',
  disable: 'Turn off',
  delete: 'Delete',
  archive: 'Archive',
  unarchive: 'Unarchive',
  moveToFolder: 'Move to folder',
};

type BulkActionBarProps = {
  selectedWorkflows: (WorkflowIdData & WorkflowStatusData)[];
  selectedFolders: FolderIdData[];
  onEnableButtonClick: (workflowIds: WorkflowId[]) => void;
  onDisableButtonClick: (workflowIds: WorkflowId[]) => void;
  onArchiveOrDeleteButtonClick: (itemIds: {
    workflowIds: WorkflowId[];
    folderIds: FolderId[];
    areAllWorkflowsDrafts?: boolean;
  }) => void;
  onMoveToFolderButtonClick: (itemIds: {
    workflowIds: WorkflowId[];
    folderIds: FolderId[];
  }) => void;
  onUnarchiveButtonClick: (workflowIds: WorkflowId[]) => void;
};

const getActions = ({
  selectedWorkflows,
  selectedFolders,
  onEnableButtonClick,
  onDisableButtonClick,
  onArchiveOrDeleteButtonClick,
  onUnarchiveButtonClick,
  onMoveToFolderButtonClick,
}: BulkActionBarProps) =>
  [
    {
      label: displayText.moveToFolder,
      icon: <FolderMove16 />,
      onClick: () =>
        onMoveToFolderButtonClick({
          workflowIds: selectedWorkflows.map(({ id }) => id),
          folderIds: selectedFolders.map(({ id }) => id),
        }),
      sortIndex: 0,
      enabled: selectedWorkflows.every(({ isArchived }) => !isArchived),
    },
    {
      label: displayText.enable,
      icon: <StartFilled />,
      onClick: () => onEnableButtonClick(selectedWorkflows.map(({ id }) => id)),
      sortIndex: 1,
      enabled:
        selectedWorkflows.every((workflow) => !workflow.enabled) &&
        selectedWorkflows.every((workflow) => !workflow.isArchived) &&
        selectedWorkflows.every((workflow) => workflow.workflowType !== WorkflowType.DRAFT) &&
        selectedFolders.length === 0,
    },
    {
      label: displayText.disable,
      icon: <Stop />,
      onClick: () => onDisableButtonClick(selectedWorkflows.map(({ id }) => id)),
      sortIndex: 2,
      enabled:
        selectedWorkflows.every((workflow) => workflow.enabled) &&
        selectedWorkflows.every((workflow) => !workflow.isArchived) &&
        selectedFolders.length === 0,
    },
    {
      label: displayText.archive,
      icon: <Archive16 />,
      onClick: () =>
        onArchiveOrDeleteButtonClick({
          workflowIds: selectedWorkflows.map(({ id }) => id),
          folderIds: selectedFolders.map(({ id }) => id),
        }),
      sortIndex: 3,
      enabled:
        !selectedFolders.length &&
        selectedWorkflows.length &&
        selectedWorkflows.every((workflow) => !workflow.isArchived) &&
        !selectedWorkflows.every((workflow) => workflow.workflowType === WorkflowType.DRAFT),
    },
    {
      label: displayText.delete,
      icon: <Delete16 />,
      onClick: () =>
        onArchiveOrDeleteButtonClick({
          workflowIds: selectedWorkflows.map(({ id }) => id),
          folderIds: selectedFolders.map(({ id }) => id),
        }),
      sortIndex: 4,
      enabled:
        (selectedFolders.length && selectedWorkflows.every((workflow) => !workflow.isArchived)) ||
        selectedWorkflows.every((workflow) => workflow.workflowType === WorkflowType.DRAFT),
    },
    {
      label: displayText.unarchive,
      icon: <Undo16 />,
      onClick: () => onUnarchiveButtonClick(selectedWorkflows.map(({ id }) => id)),
      sortIndex: 5,
      enabled:
        selectedWorkflows.every((workflow) => workflow.isArchived) && selectedFolders.length === 0,
    },
  ].filter((action) => action.enabled);

export const BulkActionBar = ({
  selectedWorkflows,
  selectedFolders,
  onEnableButtonClick,
  onDisableButtonClick,
  onArchiveOrDeleteButtonClick,
  onUnarchiveButtonClick,
  onMoveToFolderButtonClick,
}: BulkActionBarProps) => {
  const actions = useMemo(
    () =>
      getActions({
        selectedWorkflows,
        selectedFolders,
        onEnableButtonClick,
        onDisableButtonClick,
        onArchiveOrDeleteButtonClick,
        onUnarchiveButtonClick,
        onMoveToFolderButtonClick,
      }),
    [
      selectedWorkflows,
      selectedFolders,
      onEnableButtonClick,
      onDisableButtonClick,
      onArchiveOrDeleteButtonClick,
      onUnarchiveButtonClick,
    ],
  );

  return (
    <BulkActionsBar
      actions={actions}
      numSelected={selectedWorkflows.length + selectedFolders.length}
    />
  );
};
